import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { IDNow2 } from '#providers/components/idnow2';
import type { IdNowErrorKeys, IdNowStatus } from '#providers/components/idnow';
import { useToast } from '@repo/design-system-kit/components/toast/toast';
import { CFT } from '@repo/shared-kit/sentry/sentry-tags';
import { useSentry } from '@repo/shared-kit/sentry/context';
import { useReadinessSpan } from '@repo/shared-kit/hooks/use-readiness-span';
import type { ResponseError } from '@repo/api-kit/queries/utils';
import {
  kycMutations,
  KycProcessStatus,
  KycProvider,
  kycQueries,
} from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycContext } from '../context/kyc-context';
import { useKycProviderDetails } from '../context/provider-details-context';
import { useRefetchProviderDetails } from '#hooks/use-refetch-provider-details';
import { useKycTracking } from '#tracking/use-kyc-tracking';
import { getIdnowProviderDetails } from '#utils/get-idnow-provider-details';
import { SEND_SMS_TEMPLATE, TOAST_TIMEOUT } from '#constants';
import { KycError } from '#error/error';
import { isKycProviderSucceeded } from '../utils/kyc-provider-status';
import styles from './idnow-provider.module.css';

export function IdnowProvider() {
  const intl = useIntl();
  const toasts = useToast();
  const sentry = useSentry();
  const events = useKycTracking();
  const {
    hasGermanResidencePermit,
    onBack,
    onComplete,
    membershipId,
    legalCountry,
    isRegisterFlow,
    trackingPrefix,
    sendSmsMutation,
  } = useKycContext();
  const market = isRegisterFlow ? legalCountry : undefined;
  // Polling is driven by the caller (e.g. register flow polls via refetchProviderDetails;
  // non-register flows set their own refetchInterval on the parent query).
  const {
    data: kycInfo,
    refetch: refetchAcceptance,
    error: kycError,
    isError: isKycError,
  } = useSuspenseQuery(kycQueries.item({ market }));
  const refetch = useRefetchProviderDetails(refetchAcceptance);
  const cancelled = useRef(false);

  const smsMutationOptions = sendSmsMutation
    ? sendSmsMutation()
    : kycMutations.sendVerificationSms();

  const sendKycSms = useMutation({
    ...smsMutationOptions,
    onError: (error: ResponseError) => {
      if (error.status === 429) {
        return toasts.showToast(
          {
            type: 'error',
            text: intl.formatMessage({
              id: 'identity-verification-kit.kyc.toasts.download-link-sms.rate-limit',
              defaultMessage:
                "We've already sent you an SMS with a link. If you haven't received it, try again in a few minutes.",
            }),
          },
          { timeout: TOAST_TIMEOUT }
        );
      }

      return toasts.showToast(
        {
          type: 'error',
          text: intl.formatMessage({
            id: 'identity-verification-kit.kyc.toasts.download-link-sms.error',
            defaultMessage:
              'We encountered a technical issue while sending you the SMS. Could you try again?',
          }),
        },
        { timeout: TOAST_TIMEOUT }
      );
    },
    onSuccess: () => {
      events.idnowSmsSent();

      return toasts.showToast(
        {
          type: 'success',
          text: intl.formatMessage({
            id: 'identity-verification-kit.kyc.toasts.download-link-sms.success',
            defaultMessage: 'We sent you an SMS with a link.',
          }),
        },
        { timeout: TOAST_TIMEOUT }
      );
    },
  });

  const contextProviderDetails = useKycProviderDetails();
  const providerDetails = getIdnowProviderDetails(kycInfo, contextProviderDetails);
  const processStatus = kycInfo.activeProcess?.processStatus;
  const isLoading = contextProviderDetails.provider === KycProvider.idnow && !providerDetails;

  useReadinessSpan('kyc.idnow', {
    isLoading,
    isError: !providerDetails || !providerDetails.token || isKycError,
    error: (kycError ?? !providerDetails) ? new Error('Missing IDnow details') : undefined,
  });

  const handleTryAgain = useCallback(() => {
    events.idnowRegenerateLinkClicked();
    void refetch();
  }, [refetch, events]);

  const handleWaitingResults = useCallback(() => {
    events.idnowWaitingResultsScreenLoaded();
  }, [events]);

  const handleRejected = useCallback(() => {
    events.idnowRejectedScreenLoaded();
  }, [events]);

  const handleSendSMS = useCallback(async () => {
    if (!providerDetails?.token) {
      return;
    }

    await sendKycSms.mutateAsync({
      redoToken: providerDetails.token,
      templateName: SEND_SMS_TEMPLATE.IDNOW_APP_LINK,
    });
  }, [providerDetails?.token, sendKycSms]);

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (
      (isKycProviderSucceeded(providerDetails?.status) ||
        processStatus === KycProcessStatus.submitted) &&
      !cancelled.current
    ) {
      cancelled.current = true;
      onComplete(KycProvider.idnow);
    }
  }, [processStatus, providerDetails?.status, onComplete]);

  if (processStatus === KycProcessStatus.submitted) return null;

  if (isLoading) return null;

  if (!providerDetails || !providerDetails.token) {
    sentry.captureException(new Error('IDnow KYC credentials missing'), {
      extra: {
        kycCurrentStatus: kycInfo.kycCurrentStatus,
        processStatus: kycInfo.activeProcess?.processStatus,
        membershipId,
        hasProviderDetails: Boolean(providerDetails),
      },
      tags: {
        component: 'IdnowProvider',
        provider: KycProvider.idnow,
      },
      cft: CFT.IDENTITY_VERIFICATION,
    });

    return <KycError onRetry={refetch} />;
  }

  const { token, reason, status } = providerDetails;

  const qrCodeUrl = `${window.location.origin}/download-idnow2?type=qr-code&id=${token}`;

  return (
    <div className={styles.idnow2}>
      <IDNow2
        hasGermanResidencePermit={hasGermanResidencePermit}
        onBack={onBack}
        onRejected={handleRejected}
        onSendSMS={handleSendSMS}
        onWaitingResults={handleWaitingResults}
        qrCodeUrl={qrCodeUrl}
        // oxlint-disable-next-line typescript/consistent-type-assertions
        reason={reason as IdNowErrorKeys}
        // oxlint-disable-next-line typescript/consistent-type-assertions
        status={status as IdNowStatus}
        token={token}
        trackingPrefix={trackingPrefix}
        tryAgain={handleTryAgain}
      />
    </div>
  );
}
